import { Handle, Position, type NodeProps } from '@xyflow/react';
import './LineRow.css';

/**
 * ABRECHNUNG (payout_statement_event) tile rendered as the 4th column
 * (right of GS) in the Gutschrift-Check canvas (#793 phase 4).
 *
 * Header reads ``Abrechnung #<number> · <total_amount>`` so the
 * operator can correlate the GS's ``abrechnungs_number`` reference
 * with the Periodenabrechnung document at a glance. ``period_start``
 * / ``period_end`` show as a sub-header when present.
 *
 * Like BestellungNode (#784) the tile is header-only — the per-line
 * subtree (referenced Gutschriften list) isn't part of v1 column
 * scope; the GS↔Abrechnung relationship is rendered via the solid
 * arrow MatchGraph draws from each GS's ``referenz-source`` to this
 * tile's ``title-target`` handle.
 *
 * Single ``Handle`` on the LEFT edge (``Position.Left``, type
 * ``target``) so the GS-side ``referenz-source`` arrow lands on the
 * inside-facing edge — symmetric to MissingDocNode's inside-edge
 * convention.
 */
export interface PayoutStatementNodeData {
  entityId: string;
  number: string | null;
  totalAmount: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  issuerName: string | null;
  recipientName: string | null;
  // #844 — keyboard-focus ring (see LineRowNodeData.isFocused).
  isFocused?: boolean;
  // #827 — true when the materialiser surfaced this number in the
  // group's ``payoutStatementConflicts`` (≥2 distinct
  // ``payout_statement_event`` rows claim the same
  // ``abrechnungs_number``). Pre-#827 the conflict list was hydrated
  // but never read by the canvas, so the operator had no warning
  // surface explaining why both tiles existed. Optional so callers
  // that don't yet thread it (mid-pipeline rollout) keep working —
  // absent reads identical to false.
  conflictSignal?: boolean;
  [key: string]: unknown;
}

const eurFmt = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

function formatPeriod(start: string | null, end: string | null): string | null {
  if (!start && !end) return null;
  if (start && end) return `${start} – ${end}`;
  return start ?? end;
}

export function PayoutStatementNode({ data }: NodeProps) {
  const d = data as PayoutStatementNodeData;
  const titleText = d.number ? `Abrechnung #${d.number}` : 'Abrechnung';
  const totalText = d.totalAmount != null ? eurFmt.format(d.totalAmount) : null;
  const periodText = formatPeriod(d.periodStart, d.periodEnd);
  // Issuer is the Vermarkter (the processor that issues the
  // Periodenabrechnung); recipient is the farm. Show whichever is
  // available — same fallback shape as BestellungNode's supplier/buyer.
  const orgText = d.issuerName ?? d.recipientName ?? null;
  return (
    <div
      className={`line-row-node line-row-node--bestellung${d.isFocused ? ' line-row-node--focused' : ''}`}
      data-testid={`payout-statement-tile-${d.entityId}`}
      style={{
        borderColor: '#9ca3af',
        background: '#fafafa',
      }}
    >
      <Handle id="title-target" type="target" position={Position.Left} />
      <div className="line-row-node__header">
        <span className="line-row-node__doc-number">{titleText}</span>
        {totalText && <span className="line-row-node__date">{totalText}</span>}
      </div>
      {periodText && (
        <div className="line-row-node__orgs">
          <span className="line-row-node__supplier">{periodText}</span>
        </div>
      )}
      {orgText && (
        <div className="line-row-node__orgs">
          <span className="line-row-node__supplier">{orgText}</span>
        </div>
      )}
      {/* #827 — Conflict warning chip. Rendered only when the
          materialiser surfaced this ``abrechnungs_number`` as a
          duplicate-Periodenabrechnung collision in
          ``group.payoutStatementConflicts``. MatchGraph diffs the list
          against this tile's ``number`` and sets ``conflictSignal``;
          the chip is the visible "why are two Abrechnung tiles
          claiming the same number?" affordance. ``role=status``
          surfaces it to screen readers without stealing focus. */}
      {d.conflictSignal && (
        <div
          className="line-row-node__orgs"
          role="status"
          data-testid={`payout-statement-tile-${d.entityId}-conflict-chip`}
          style={{ color: '#b45309' }}
        >
          <span className="line-row-node__supplier">
            ⚠ Doppelte Nummer
          </span>
        </div>
      )}
    </div>
  );
}
